import {
  ChangeDetectorRef,
  Component,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { environment } from '../../../environment/environment';

interface CleaningUniqueLink {
  link_uuid: string;
  created_at: string | null;
  link_path: string | null;
}

@Component({
  selector: 'app-duna-cleaning-unique-links',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatTooltipModule,
  ],
  templateUrl: './duna-cleaning-unique-links.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './duna-cleaning-unique-links.component.scss',
})
export class DunaCleaningUniqueLinksComponent implements OnInit {
  loading = false;
  creating = false;
  errorMessage: string | null = null;
  copiedLink: string | null = null;

  tableDataSource = new MatTableDataSource<CleaningUniqueLink>([]);
  displayedColumns = ['created_at', 'link', 'actions'];

  constructor(private http: HttpClient, private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.loadLinks();
  }

  createLink(): void {
    this.creating = true;
    this.errorMessage = null;
    this.http
      .post<CleaningUniqueLink>(
        `${environment.apiUrl}/events/cleaning/unique-links`,
        {}
      )
      .subscribe({
        next: (created) => {
          this.creating = false;
          if (created?.link_uuid) {
            const rows = [created, ...this.tableDataSource.data];
            this.tableDataSource.data = rows;
          }
          this.cdr.detectChanges();
        },
        error: (error) => {
          this.creating = false;
          this.errorMessage = this.formatApiError(
            error,
            'Kunne ikke oprette link.'
          );
          console.error('[duna-cleaning-unique-links] POST failed', error);
          this.cdr.detectChanges();
        },
      });
  }

  deleteLink(row: CleaningUniqueLink): void {
    if (!row.link_uuid) return;
    if (
      !confirm(
        'Slet dette rengørings-link? Offentlig adgang til listen tilbagekaldes.'
      )
    )
      return;

    this.http
      .delete(
        `${
          environment.apiUrl
        }/events/cleaning/unique-links/${encodeURIComponent(row.link_uuid)}`
      )
      .subscribe({
        next: () => {
          this.tableDataSource.data = this.tableDataSource.data.filter(
            (r) => r.link_uuid !== row.link_uuid
          );
          this.cdr.detectChanges();
        },
        error: (error) => {
          this.errorMessage = this.formatApiError(
            error,
            'Kunne ikke slette link.'
          );
          console.error('[duna-cleaning-unique-links] DELETE failed', error);
          this.cdr.detectChanges();
        },
      });
  }

  copyLink(item: CleaningUniqueLink): void {
    const absolute = this.absoluteLink(item);
    if (!absolute) return;
    navigator.clipboard?.writeText(absolute).then(() => {
      this.copiedLink = absolute;
      setTimeout(() => {
        if (this.copiedLink === absolute) this.copiedLink = null;
      }, 2000);
    });
  }

  absoluteLink(item: CleaningUniqueLink): string | null {
    if (!item.link_uuid) return null;
    return `${window.location.origin}/cleaning/${item.link_uuid}`;
  }

  private loadLinks(): void {
    this.loading = true;
    this.errorMessage = null;
    this.http
      .get<{ items: CleaningUniqueLink[] }>(
        `${environment.apiUrl}/events/cleaning/unique-links`
      )
      .subscribe({
        next: (response) => {
          this.loading = false;
          this.tableDataSource.data = response?.items || [];
          this.cdr.detectChanges();
        },
        error: (error) => {
          this.loading = false;
          this.tableDataSource.data = [];
          this.errorMessage = this.formatApiError(
            error,
            'Kunne ikke hente links.'
          );
          console.error('[duna-cleaning-unique-links] list load failed', error);
          this.cdr.detectChanges();
        },
      });
  }

  private formatApiError(error: unknown, fallback: string): string {
    const httpErr = error as { error?: unknown; message?: string };
    const body = httpErr?.error;

    if (typeof body === 'string') {
      try {
        const parsed = JSON.parse(body) as Record<string, unknown>;
        if (parsed && typeof parsed === 'object') {
          return this.formatApiError({ ...httpErr, error: parsed }, fallback);
        }
      } catch {
        return body.trim() || fallback;
      }
    }

    if (body && typeof body === 'object') {
      const o = body as { message?: string; error?: string; detail?: string };
      const tech = (o.detail || o.error || '').trim();
      const msg = (o.message || '').trim();
      if (tech && msg) return `${msg}: ${tech}`;
      return tech || msg || fallback;
    }

    return (httpErr?.message || '').trim() || fallback;
  }
}
